/* ============================================================
   Diagnostic domains — 8 domains, 32 questions, weighted scoring.

   Two audit fixes are baked into the data shape here:

   A-08  "Not applicable" options now carry `score: null` instead of 2.
         A null score removes the question from that domain's denominator
         entirely. Previously an operator with no bonded warehouse scored
         50% on the bonded-warehouse question — a fabricated number
         sitting inside a weighted index.

   A-09  The fuel-share band has been re-based. The scaffold scored
         ">45% fuel share" as 0/4, but the Kenya Transporters Association
         puts fuel at ~55% of road-freight operating cost as the sector
         norm — so every honest Kenyan road operator scored zero on the
         question, and the top band was unreachable for a road fleet.

   Questions that map to a specific instrument carry `regulationId`, so
   the UI can render live status rather than baked-in future tense.
   ============================================================ */

/* ============================================================
   Domain context.

   A weighted index of 62 is a number, not a diagnosis. Until now the
   scorecard told an operator where they scored and nothing about what
   the sector does — so nobody could tell whether 62 was a crisis or a
   good quarter. The sister platform solved this years ago: every one of
   its eight domains carries a sourced benchmark statement beside the
   score. This is that, pointed the other way.

   THE RULE: a domain carries context only where a real, citable source
   exists. Five of eight do. The other three show nothing rather than a
   plausible-sounding sentence — a fabricated benchmark is worse than an
   absent one, because it invites a comparison that means nothing. The
   data gate enforces the same thing from the other side: any domain
   that declares `context` must also carry `source` and `asOf`, and the
   date is checked for staleness exactly like the corridor bands.
   ============================================================ */

/** Context older than this stops being current and starts being lore. */
export const CONTEXT_MAX_AGE_DAYS = 540;

export const DOMAINS = [
  {
    id: 'customs',
    name: 'Customs & clearance',
    weight: 16,
    context:
      'Kenya Revenue Authority reports cargo clearance falling from around nine days to ' +
      'under three for compliant traders since iCMS. Authorised Economic Operator status ' +
      'adds automatic passing of declarations and priority in the clearance chain — the ' +
      'gap between an accredited operator and an unaccredited one is now days, not hours.',
    source: 'Kenya Revenue Authority — iCMS and AEO programme',
    sourceUrl: 'https://www.kra.go.ke/business/authorized-economic-operators-aeo/learn-about-aeo/benefits-of-aeo',
    asOf: '2026-08-02',
    color: 'var(--ca-chart-8)',
    questions: [
      {
        id: 'customs-clearance-time',
        text: 'How long does a typical Mombasa sea freight clearance take, from vessel arrival to release?',
        options: [
          { label: '6+ days, including inspections and delays', score: 0 },
          { label: '4–5 days, with some queries or red channel', score: 1 },
          { label: '2–3 days, green or yellow channel', score: 2 },
          { label: '1–2 days, pre-clearance lodged and documents clean', score: 3 },
          { label: 'Same or next day, IDF pre-filed with an established agent', score: 4 }
        ]
      },
      {
        id: 'customs-idf-prefile',
        regulationId: 'idf-2-5pc',
        text: 'Do you file the Import Declaration Form before goods leave the origin country?',
        options: [
          { label: 'Never — we file after arrival', score: 0 },
          { label: 'Sometimes, when we remember', score: 1 },
          { label: 'Usually, but not systematically', score: 2 },
          { label: 'Always, for most shipments', score: 3 },
          { label: 'Always, through an automated pre-clearance workflow', score: 4 }
        ]
      },
      {
        id: 'customs-red-channel',
        text: 'What share of your shipments face KRA physical (red channel) inspection?',
        options: [
          { label: 'More than 30% — frequent reassessments', score: 0 },
          { label: '15–30% — regular but manageable', score: 1 },
          { label: '5–15% — occasional spot checks', score: 2 },
          { label: 'Under 5% — mostly green or yellow', score: 3 },
          { label: 'Under 2% — clean compliance record', score: 4 }
        ]
      },
      {
        id: 'customs-agent-licence',
        regulationId: 'kra-customs-agent-licence',
        text: 'Is your clearing agent KRA-licensed, and do you verify the licence annually?',
        options: [
          { label: 'No formal agent, or attempting to self-clear', score: 0 },
          { label: 'Agent used, licence never checked', score: 1 },
          { label: 'Licensed agent, checked once', score: 2 },
          { label: 'Licensed agent, checked annually', score: 3 },
          { label: 'Multiple vetted agents, performance tracked', score: 4 }
        ]
      }
    ]
  },

  {
    id: 'fleet',
    name: 'Fleet & transport',
    weight: 14,
    context:
      'Empty running is the corridor\u2019s defining inefficiency: NCTTCA attributes 59% of the ' +
      'greenhouse gas emissions of a return journey to the empty leg against 41% for the ' +
      'loaded one. A fleet without a backhaul strategy is paying for slightly more than half ' +
      'its distance twice \u2014 once in fuel and once in the carbon a shipper now asks about.',
    source: 'NCTTCA — Northern Corridor Green Freight Strategy 2030',
    sourceUrl: 'https://www.ttcanc.org/multiple-impact-empty-freight-transport-trips-along-northern-corridor',
    asOf: '2026-08-02',
    color: 'var(--ca-chart-2)',
    questions: [
      {
        id: 'fleet-cvrl',
        regulationId: 'ntsa-cvrl',
        text: 'Do all your commercial vehicles hold a current NTSA Commercial Vehicle Road Licence?',
        options: [
          { label: 'Not applicable — we operate no commercial vehicles', score: null },
          { label: 'No — significant gaps in licensing', score: 0 },
          { label: 'Some licensed, many expired', score: 1 },
          { label: 'Most current, some renewal backlog', score: 2 },
          { label: 'All current, renewals tracked manually', score: 3 },
          { label: 'All current, with automated renewal alerts', score: 4 }
        ]
      },
      {
        id: 'fleet-telematics',
        text: 'Are telematics systems installed and NTSA-compliant across the fleet?',
        options: [
          { label: 'Not applicable — we operate no commercial vehicles', score: null },
          { label: 'No telematics on any vehicle', score: 0 },
          { label: 'Basic GPS on some vehicles', score: 1 },
          { label: 'GPS on most, not to NTSA standard', score: 2 },
          { label: 'NTSA-compliant telematics on all vehicles', score: 3 },
          { label: 'Integrated fleet platform with driver scoring', score: 4 }
        ]
      },
      {
        id: 'fleet-maintenance-records',
        text: 'Do you keep vehicle service and repair records for at least two years?',
        options: [
          { label: 'Not applicable — we operate no commercial vehicles', score: null },
          { label: 'No formal records kept', score: 0 },
          { label: 'Informal or paper records only', score: 1 },
          { label: 'Digital records, but incomplete', score: 2 },
          { label: 'Complete digital records, two years or more', score: 3 },
          { label: 'Integrated workshop management system', score: 4 }
        ]
      },
      {
        id: 'fleet-age',
        text: 'What is the average age and condition of your fleet?',
        options: [
          { label: 'Not applicable — we operate no commercial vehicles', score: null },
          { label: 'Mostly over 15 years, frequent breakdowns', score: 0 },
          { label: '10–15 years, regular mechanical issues', score: 1 },
          { label: '7–10 years, acceptable condition', score: 2 },
          { label: '5–7 years, well maintained', score: 3 },
          { label: 'Under 5 years, on a preventive maintenance schedule', score: 4 }
        ]
      }
    ]
  },

  {
    id: 'port',
    name: 'Port & ICD efficiency',
    weight: 14,
    context:
      'Containerised dwell at Mombasa fell from about 6.5 days in 2011 to roughly 70 hours ' +
      'by 2017 on the Northern Corridor Transport Observatory measure — discharge to exit ' +
      'after clearance, which includes time at a CFS. Anything materially above three days ' +
      'is now an outlier rather than the norm.',
    source: 'NCTTCA Northern Corridor Transport Observatory',
    sourceUrl: 'https://top.ttcanc.org/indicators',
    asOf: '2026-08-02',
    color: 'var(--ca-chart-1)',
    questions: [
      {
        id: 'port-dwell',
        text: 'What is your average container dwell time at Mombasa Port or an ICD before exit?',
        options: [
          { label: 'Over 14 days — chronic delays', score: 0 },
          { label: '8–14 days — demurrage regularly incurred', score: 1 },
          { label: '5–7 days — within free period most times', score: 2 },
          { label: '3–4 days — efficient coordination', score: 3 },
          { label: 'Under 3 days — pre-cleared with truck ready', score: 4 }
        ]
      },
      {
        id: 'port-demurrage-tracking',
        text: 'Do you track and budget demurrage and storage costs monthly?',
        options: [
          { label: 'No tracking — the costs are a surprise', score: 0 },
          { label: 'Aware of the costs but not tracked', score: 1 },
          { label: 'Tracked retrospectively', score: 2 },
          { label: 'Tracked monthly with variance analysis', score: 3 },
          { label: 'Tracked in real time with predictive alerts', score: 4 }
        ]
      },
      {
        id: 'port-sgr-use',
        text: 'Do you use the SGR for Nairobi-bound containerised cargo?',
        options: [
          { label: 'Not applicable — no Nairobi-bound containerised cargo', score: null },
          { label: 'Never — everything is trucked from Mombasa', score: 0 },
          { label: 'Rarely — too unreliable or expensive for us', score: 1 },
          { label: 'Sometimes — depends on the cargo', score: 2 },
          { label: 'Often — rail is primary for standard containers', score: 3 },
          { label: 'Always — mode optimised per shipment', score: 4 }
        ]
      },
      {
        id: 'port-tariff-2025',
        regulationId: 'kpa-tariff-2025',
        text: 'Have you rebased pricing and contracts on the KPA Tariff 2025 rates?',
        options: [
          { label: 'Unaware of the change', score: 0 },
          { label: 'Aware, no action taken', score: 1 },
          { label: 'Impact reviewed on key services', score: 2 },
          { label: 'Pricing and contracts revised', score: 3 },
          { label: 'Full cost model updated and clients informed', score: 4 }
        ]
      }
    ]
  },

  {
    id: 'cost',
    name: 'Cost & margin',
    weight: 13,
    context:
      'The Kenya Transporters Association puts fuel at roughly 55% of road-freight operating ' +
      'cost, and it moved 13\u201314% inside a single cycle in 2026. An operator who cannot ' +
      'restate their cost per tonne-kilometre within a week of a fuel move is quoting on a ' +
      'number that has already expired.',
    source: 'Kenya Transporters Association pricing guidance',
    sourceUrl: 'https://capitalfm.africa/transport-costs-to-rise-as-kta-issues-new-pricing-guidance-after-sharp-fuel-hike/',
    asOf: '2026-08-02',
    color: 'var(--ca-chart-3)',
    questions: [
      {
        id: 'cost-per-ton-km',
        text: 'Do you know your cost per ton-kilometre by corridor and mode?',
        options: [
          { label: 'No idea — pricing is guesswork', score: 0 },
          { label: 'A rough estimate only', score: 1 },
          { label: 'Calculated annually', score: 2 },
          { label: 'Calculated quarterly by route', score: 3 },
          { label: 'Tracked per shipment', score: 4 }
        ]
      },
      {
        id: 'cost-fuel-share',
        // Bands re-based on the KTA figure of ~55% as the Kenyan road norm.
        text: 'What share of your operating cost is fuel?',
        note: 'The Kenya Transporters Association puts fuel at roughly 55% of road-freight operating cost, so the bands below are set against that norm rather than a global average.',
        options: [
          { label: 'Over 65% — severely exposed', score: 0 },
          { label: '55–65% — above the Kenyan road norm', score: 1 },
          { label: '45–55% — around the Kenyan road norm', score: 2 },
          { label: '35–45% — better than the road norm', score: 3 },
          { label: 'Under 35% — multimodal or fuel-hedged', score: 4 }
        ]
      },
      {
        id: 'cost-empty-backhaul',
        text: 'What is your typical empty backhaul percentage?',
        options: [
          { label: 'Not applicable — we do not run our own vehicles', score: null },
          { label: 'Over 50% — most return trips run empty', score: 0 },
          { label: '35–50% — significant waste', score: 1 },
          { label: '20–35% — some backhaul matching', score: 2 },
          { label: '10–20% — actively sourcing backhaul', score: 3 },
          { label: 'Under 10% — demand-matched routing', score: 4 }
        ]
      },
      {
        id: 'cost-levy-passthrough',
        regulationId: 'rdl-2pc',
        text: 'Are the 2% RDL and KPA Tariff 2025 reflected in your current pricing?',
        options: [
          { label: 'No — still pricing on old rates', score: 0 },
          { label: 'Aware, not yet passed through', score: 1 },
          { label: 'Partial pass-through to some clients', score: 2 },
          { label: 'Full pass-through, contracts renegotiated', score: 3 },
          { label: 'Dynamic pricing model with levy tracking', score: 4 }
        ]
      }
    ]
  },

  {
    id: 'safety',
    name: 'Regulatory & safety',
    weight: 12,
    color: 'var(--ca-chart-4)',
    questions: [
      {
        id: 'safety-cvol',
        regulationId: 'ntsa-cvol',
        text: 'Do you hold a current NTSA Commercial Vehicle Operator Licence?',
        options: [
          { label: 'Not applicable — we are not a vehicle operator', score: null },
          { label: 'No — was not aware of the requirement', score: 0 },
          { label: 'Aware, not yet applied', score: 1 },
          { label: 'Application in progress', score: 2 },
          { label: 'Licence held', score: 3 },
          { label: 'Licence held and a safety programme is active', score: 4 }
        ]
      },
      {
        id: 'safety-accident-protocol',
        text: 'Do you have a documented fatal accident reporting protocol?',
        options: [
          { label: 'No protocol exists', score: 0 },
          { label: 'Informal understanding only', score: 1 },
          { label: 'Documented but never tested', score: 2 },
          { label: 'Documented with roles assigned', score: 3 },
          { label: 'Drilled, with 24/7 response capability', score: 4 }
        ]
      },
      {
        id: 'safety-driver-hours',
        text: 'Are drivers qualified, certified and within legal driving-hour limits?',
        options: [
          { label: 'Not applicable — we employ no drivers', score: null },
          { label: 'No formal qualification checks', score: 0 },
          { label: 'Licences checked at hire only', score: 1 },
          { label: 'Licences current, hours loosely tracked', score: 2 },
          { label: 'Licences verified annually, hours logged', score: 3 },
          { label: 'Digital tachograph with fatigue monitoring', score: 4 }
        ]
      },
      {
        id: 'safety-training',
        text: 'How often do you run road safety programmes for drivers?',
        options: [
          { label: 'Not applicable — we employ no drivers', score: null },
          { label: 'Never', score: 0 },
          { label: 'Once, at onboarding', score: 1 },
          { label: 'Annually', score: 2 },
          { label: 'Quarterly', score: 3 },
          { label: 'Monthly, plus incident-triggered refreshers', score: 4 }
        ]
      }
    ]
  },

  {
    id: 'docs',
    name: 'Documentation',
    weight: 11,
    color: 'var(--ca-chart-6)',
    questions: [
      {
        id: 'docs-pvoc',
        regulationId: 'kebs-pvoc',
        text: 'Is your PVoC / Certificate of Conformity process managed before shipment departs?',
        options: [
          { label: 'Not applicable — we do not import regulated goods', score: null },
          { label: 'No — destination inspection only', score: 0 },
          { label: 'Sometimes, reactively', score: 1 },
          { label: 'Usually, but delays occur', score: 2 },
          { label: 'Always, with suppliers briefed pre-order', score: 3 },
          { label: 'Automated, supplier-integrated workflow', score: 4 }
        ]
      },
      {
        id: 'docs-hs-classification',
        text: 'Are your imports correctly HS-classified against verified reference values?',
        options: [
          { label: 'Not applicable — we do not import', score: null },
          { label: 'No — frequent misclassification issues', score: 0 },
          { label: 'The agent handles it; we do not verify', score: 1 },
          { label: 'Spot-checked occasionally', score: 2 },
          { label: 'Verified for all regulated goods', score: 3 },
          { label: 'Maintained HS database mapped to KRA reference values', score: 4 }
        ]
      },
      {
        id: 'docs-acd',
        regulationId: 'kra-acd',
        text: 'Is the Advance Cargo Declaration reference obtained before loading at origin?',
        options: [
          { label: 'Not applicable — we do not import by sea', score: null },
          { label: 'No — we were not aware of the requirement', score: 0 },
          { label: 'Aware, no process in place', score: 1 },
          { label: 'Process being designed', score: 2 },
          { label: 'Workflow in place and agents briefed', score: 3 },
          { label: 'Integrated into booking, with the BoL field enforced', score: 4 }
        ]
      },
      {
        id: 'docs-bonded-warehouse',
        regulationId: 'kra-bonded-warehouse',
        text: 'If you operate a bonded warehouse, is the licence current and the CB6 bond active?',
        options: [
          { label: 'Not applicable — we operate no bonded warehouse', score: null },
          { label: 'Bonded warehouse operated, licence expired', score: 0 },
          { label: 'Licence current, bond status unclear', score: 1 },
          { label: 'Licence current, bond active', score: 3 },
          { label: 'Licence current, bond active, annual audit clean', score: 4 }
        ]
      }
    ]
  },

  {
    id: 'corridor',
    name: 'Corridor & network',
    weight: 10,
    context:
      'The Northern Corridor moves on the order of 75,000 tonnes a day from Mombasa to the ' +
      'hinterland in roughly 2,000\u20133,000 truck movements. Scheduling against that flow, ' +
      'rather than against your own fleet in isolation, is what separates a network from a ' +
      'set of trips.',
    source: 'NCTTCA Northern Corridor Transport Observatory',
    sourceUrl: 'https://top.ttcanc.org/indicators',
    asOf: '2026-08-02',
    color: 'var(--ca-chart-5)',
    questions: [
      {
        id: 'corridor-multimodal',
        text: 'Do you actively use multi-modal transport across road, rail, port and ICD?',
        options: [
          { label: 'Road only — single mode', score: 0 },
          { label: 'Road plus occasional rail', score: 1 },
          { label: 'Road and rail, selected by cargo type', score: 2 },
          { label: 'Optimised multi-modal per shipment', score: 3 },
          { label: 'Dynamic mode switching on live data', score: 4 }
        ]
      },
      {
        id: 'corridor-route-optimisation',
        text: 'Do you optimise routes on actual cost, time and risk data?',
        options: [
          { label: 'No — fixed routes, the driver decides', score: 0 },
          { label: 'Informal route knowledge', score: 1 },
          { label: 'Basic route planning', score: 2 },
          { label: 'Data-driven route optimisation', score: 3 },
          { label: 'Algorithmic route optimisation', score: 4 }
        ]
      },
      {
        id: 'corridor-eac-procedures',
        regulationId: 'eac-cma-2004',
        text: 'How well documented are your EAC cross-border procedures?',
        options: [
          { label: 'Not applicable — no cross-border operations', score: null },
          { label: 'Cross-border, but documentation is ad hoc', score: 0 },
          { label: 'Basic understanding, agent-dependent', score: 1 },
          { label: 'Some procedures written down', score: 2 },
          { label: 'Documented procedures per corridor', score: 3 },
          { label: 'EAC compliance specialist on the team', score: 4 }
        ]
      },
      {
        id: 'corridor-demand-forecast',
        text: 'Do you forecast demand to reduce empty backhaul?',
        options: [
          { label: 'Not applicable — we do not run our own vehicles', score: null },
          { label: 'No forecasting — reactive dispatch', score: 0 },
          { label: 'Informal customer check-ins', score: 1 },
          { label: 'Monthly demand review', score: 2 },
          { label: 'Weekly forecasting with key clients', score: 3 },
          { label: 'Integrated demand forecasting', score: 4 }
        ]
      }
    ]
  },

  {
    id: 'financial',
    name: 'Financial health',
    weight: 10,
    color: 'var(--ca-chart-7)',
    questions: [
      {
        id: 'fin-duty-capital',
        text: 'Do you have working capital ready for duty the moment cargo arrives?',
        options: [
          { label: 'Not applicable — we do not pay duty', score: null },
          { label: 'No — frequent delays waiting for funds', score: 0 },
          { label: 'Sometimes, depending on cash flow', score: 1 },
          { label: 'Usually, but margins are tight', score: 2 },
          { label: 'Always — a dedicated customs reserve', score: 3 },
          { label: 'Pre-arranged duty credit facility', score: 4 }
        ]
      },
      {
        id: 'fin-credit-facilities',
        text: 'Do you have credit or deferred payment arrangements with KPA or shipping lines?',
        options: [
          { label: 'No — everything paid upfront', score: 0 },
          { label: 'Informal arrangements only', score: 1 },
          { label: 'One facility in place', score: 2 },
          { label: 'Multiple facilities, actively managed', score: 3 },
          { label: 'Integrated trade finance facility', score: 4 }
        ]
      },
      {
        id: 'fin-insurance',
        text: 'Is your insurance cover — cargo, fleet, liability — adequate and current?',
        options: [
          { label: 'No formal insurance review', score: 0 },
          { label: 'Basic third-party only', score: 1 },
          { label: 'Comprehensive, not reviewed annually', score: 2 },
          { label: 'Comprehensive, reviewed annually', score: 3 },
          { label: 'Risk-assessed, benchmarked, gaps covered', score: 4 }
        ]
      },
      {
        id: 'fin-capex-plan',
        text: 'Do you have a funded fleet replacement and expansion plan?',
        options: [
          { label: 'Not applicable — we own no vehicles', score: null },
          { label: 'No plan — replace when something breaks', score: 0 },
          { label: 'An informal wish list', score: 1 },
          { label: 'A plan, partially funded', score: 2 },
          { label: 'A plan, fully funded or financed', score: 3 },
          { label: 'Five-year CAPEX plan with lender relationships', score: 4 }
        ]
      }
    ]
  }
];

/* ---- Derived accessors ---- */

let flatCache = null;

export function getAllQuestions() {
  if (flatCache) return flatCache;
  flatCache = [];
  for (const domain of DOMAINS) {
    for (const question of domain.questions) {
      flatCache.push({ ...question, domainId: domain.id, domainName: domain.name });
    }
  }
  return flatCache;
}

export function getTotalQuestions() {
  return getAllQuestions().length;
}

export function getQuestionAtIndex(index) {
  return getAllQuestions()[index] || null;
}

export function getDomainById(id) {
  return DOMAINS.find((d) => d.id === id) || null;
}

export function totalWeight() {
  return DOMAINS.reduce((sum, d) => sum + d.weight, 0);
}
